import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MessageSquare, Search, Edit2, Trash2, Save, FileText } from 'lucide-react';
import { toast } from 'sonner';

const AdminCommentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {id, content, reader_name, article_title}
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) { navigate('/login'); return; }
    load();
  }, []);

  const load = async (q = '') => {
    setLoading(true);
    try {
      const url = q ? `/admin/comments?search=${encodeURIComponent(q)}&limit=300` : '/admin/comments?limit=300';
      const res = await api.get(url);
      setComments(res.data || []);
    } catch {
      toast.error('فشل تحميل التعليقات');
    } finally { setLoading(false); }
  };

  const handleSearch = () => load(search);

  const handleEdit = (c) => {
    setEditing(c);
    setEditText(c.content || c.text || '');
  };

  const submitEdit = async () => {
    if (!editText.trim()) { toast.error('نص التعليق فارغ'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/comments/${editing.id}`, { content: editText.trim() });
      toast.success('تم تعديل التعليق');
      setEditing(null);
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التعديل');
    } finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`حذف تعليق ${c.reader_name || 'القارئ'} على "${c.article_title || ''}"؟`)) return;
    try {
      await api.delete(`/admin/comments/${c.id}`);
      toast.success('تم الحذف');
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحذف');
    }
  };

  if (loading && comments.length === 0) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-comments-page">
      <div className="flex items-center gap-3">
        <MessageSquare className="w-8 h-8 text-emerald-700" />
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-accent">إدارة تعليقات المقالات</h1>
          <p className="text-sm text-muted-foreground">{comments.length} تعليق</p>
        </div>
      </div>

      <Card className="p-4 flex gap-2 items-center">
        <Search className="w-5 h-5 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="بحث في النص أو اسم القارئ أو رقمه..."
          className="flex-1 h-10"
          data-testid="comments-search-input"
        />
        <Button onClick={handleSearch} className="bg-primary" data-testid="comments-search-btn">بحث</Button>
        {search && (
          <Button onClick={() => { setSearch(''); load(''); }} variant="outline" data-testid="comments-clear-btn">إعادة</Button>
        )}
      </Card>

      {comments.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="w-16 h-16 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">لا توجد تعليقات مطابقة</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <Card key={c.id} className="p-4" data-testid={`comment-card-${c.id}`}>
              <div className="flex flex-col md:flex-row md:items-start gap-3">
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-bold text-accent">{c.reader_name || 'قارئ'}</span>
                    <span className="text-muted-foreground">{c.reader_number || ''}</span>
                    {c.article_title && (
                      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full font-bold">
                        <FileText className="w-3 h-3" /> {c.article_title}
                      </span>
                    )}
                    <span className="text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleString('ar') : ''}</span>
                    {c.edited_at && (
                      <span className="text-amber-600 italic">معدّل</span>
                    )}
                  </div>
                  <p className="text-sm text-foreground whitespace-pre-wrap break-words">{c.content || c.text}</p>
                </div>
                <div className="flex md:flex-col gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(c)} data-testid={`edit-comment-${c.id}`}>
                    <Edit2 className="w-4 h-4 ml-1" /> تعديل
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(c)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-comment-${c.id}`}>
                    <Trash2 className="w-4 h-4 ml-1" /> حذف
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle>تعديل التعليق</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                <span className="font-bold">{editing.reader_name}</span> على «{editing.article_title || ''}»
              </p>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full h-40 border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                dir="rtl"
                data-testid="edit-comment-textarea"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-cancel-btn">إلغاء</Button>
                <Button onClick={submitEdit} disabled={saving} className="bg-primary" data-testid="edit-save-btn">
                  <Save className="w-4 h-4 ml-1" /> {saving ? 'جارٍ الحفظ...' : 'حفظ'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommentsPage;
